/**
 * Shadow DOM chat widget.
 * Phase 2 deliverable — bottom-right bubble, panel, inline mount,
 * mobile bottom-sheet, theming, and accessibility.
 */

import type { CoignConfig, CoignTheme } from '../types.js';
import styles from './styles.css?inline';
import { attachLoadingOverlay, detachLoadingOverlay } from './loading-overlay.js';
import { on } from '../events.js';

/* ------------------------------------------------------------------ */
/*  Module state                                                      */
/* ------------------------------------------------------------------ */

let hostElement: HTMLElement | null = null;
let shadowRoot: ShadowRoot | null = null;
let bubbleEl: HTMLButtonElement | null = null;
let panelEl: HTMLElement | null = null;
let messagesEl: HTMLElement | null = null;
let formEl: HTMLFormElement | null = null;
let inputEl: HTMLInputElement | null = null;
let onSubmitCallback: ((question: string) => void) | null = null;
let isPanelOpen = false;
let isInline = false;
let originalParent: HTMLElement | null = null;
let focusTrapCleanup: (() => void) | null = null;
let lastFocusedElement: Element | null = null;
let resizeHandler: (() => void) | null = null;
let keydownHandler: ((e: KeyboardEvent) => void) | null = null;
let isThinking = false;
let thinkingMsgEl: HTMLElement | null = null;
let streamMsgEl: HTMLElement | null = null;
let streamTextEl: HTMLElement | null = null;
let onOpenCallback: (() => void) | null = null;
let upgradeStatusEl: HTMLElement | null = null;

/* ------------------------------------------------------------------ */
/*  DOM helpers                                                       */
/* ------------------------------------------------------------------ */

function createEl(tag: string, cls?: string, attrs?: Record<string, string>): HTMLElement {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}

/* ------------------------------------------------------------------ */
/*  createWidget                                                      */
/* ------------------------------------------------------------------ */

export function createWidget(config: CoignConfig, onSubmit?: (question: string) => void): void {
  if (hostElement) return; // already initialised

  onSubmitCallback = onSubmit ?? null;

  hostElement = createEl('div', undefined, { id: 'coign-widget-host' });

  shadowRoot = hostElement.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadowRoot.appendChild(styleEl);

  const root = createEl('div', 'coign-root coign-widget', {
    'data-position': config.position ?? 'bottom-right',
  });
  shadowRoot.appendChild(root);

  applyTheme(config.theme, root);

  // ---- Bubble -------------------------------------------------------
  bubbleEl = document.createElement('button');
  bubbleEl.className = 'coign-bubble';
  bubbleEl.setAttribute('aria-label', 'Open chat');
  bubbleEl.innerHTML =
    '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/></svg>';
  bubbleEl.addEventListener('click', openPanel);
  root.appendChild(bubbleEl);

  // ---- Panel --------------------------------------------------------
  panelEl = createEl('div', 'coign-panel', {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'coign-panel-title',
  });

  // Header
  const header = createEl('div', 'coign-panel-header');

  const titleWrap = createEl('div', 'coign-panel-title-wrap');
  const statusDot = createEl('span', 'coign-status-dot', { 'aria-hidden': 'true' });
  // Shell-first: idle until engage starts a download
  statusDot.dataset.status = 'ready';
  titleWrap.appendChild(statusDot);
  const title = createEl('h2', 'coign-panel-title', { id: 'coign-panel-title' });
  title.textContent = 'Coign';
  titleWrap.appendChild(title);
  upgradeStatusEl = createEl('span', 'coign-upgrade-status');
  upgradeStatusEl.textContent = '';
  titleWrap.appendChild(upgradeStatusEl);
  header.appendChild(titleWrap);

  const closeBtn = createEl('button', 'coign-close', { 'aria-label': 'Close chat' });
  closeBtn.textContent = '\u00d7';
  closeBtn.addEventListener('click', closePanel);
  header.appendChild(closeBtn);
  panelEl.appendChild(header);

  // Messages
  messagesEl = createEl('div', 'coign-messages', {
    role: 'log',
    'aria-live': 'polite',
    'aria-relevant': 'additions',
  });
  panelEl.appendChild(messagesEl);

  // Welcome message
  if (config.welcome) {
    const welcomeText = typeof config.welcome === 'string' ? config.welcome : config.welcome.message;
    if (welcomeText) addAssistantMessage(welcomeText);
  }

  // Form
  formEl = document.createElement('form');
  formEl.className = 'coign-form';

  inputEl = document.createElement('input');
  inputEl.className = 'coign-input';
  inputEl.type = 'text';
  inputEl.placeholder = 'Ask a question\u2026';
  inputEl.setAttribute('aria-label', 'Message');
  formEl.appendChild(inputEl);

  const sendBtn = createEl('button', 'coign-send', {
    type: 'submit',
    'aria-label': 'Send',
  });
  sendBtn.innerHTML =
    '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="16" height="16"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>';
  formEl.appendChild(sendBtn);

  formEl.addEventListener('submit', (e) => {
    e.preventDefault();
    if (isThinking) return;
    const value = inputEl!.value.trim();
    if (!value) return;
    inputEl!.value = '';
    addUserMessage(value);
    setThinking(true);
    if (onSubmitCallback) onSubmitCallback(value);
  });

  panelEl.appendChild(formEl);
  root.appendChild(panelEl);

  // Attach loading overlay inside the panel (shows on engage, not on init)
  attachLoadingOverlay(panelEl);

  // Status dot follows engine state
  on('downloadStart', () => setStatus('loading'));
  on('modelReady', () => setStatus('ready'));
  on('ready', () => setStatus('ready'));
  on('error', () => setStatus('error'));

  document.body.appendChild(hostElement);

  // ---- Global listeners ---------------------------------------------
  keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isPanelOpen) {
      e.stopPropagation();
      closePanel();
    }
  };
  document.addEventListener('keydown', keydownHandler);

  resizeHandler = () => {
    const isMobile = window.innerWidth < 720;
    root.classList.toggle('coign-mobile', isMobile);
  };
  window.addEventListener('resize', resizeHandler);
  resizeHandler();
}

/* ------------------------------------------------------------------ */
/*  Thinking / status helpers                                         */
/* ------------------------------------------------------------------ */

export function setThinking(thinking: boolean): void {
  isThinking = thinking;

  if (inputEl) {
    inputEl.disabled = thinking;
    inputEl.placeholder = thinking ? 'Thinking\u2026' : 'Ask a question\u2026';
  }

  const sendBtn = formEl?.querySelector('.coign-send') as HTMLButtonElement | null;
  if (sendBtn) sendBtn.disabled = thinking;

  if (thinking) {
    if (!thinkingMsgEl) {
      thinkingMsgEl = createEl('div', 'coign-message coign-message--assistant coign-message--thinking');
      const avatar = createEl('div', 'coign-message-avatar');
      avatar.textContent = 'A';
      thinkingMsgEl.appendChild(avatar);
      const contentWrap = createEl('div', 'coign-message-content');
      const textEl = createEl('div', 'coign-message-text');
      textEl.textContent = 'Thinking\u2026';
      contentWrap.appendChild(textEl);
      thinkingMsgEl.appendChild(contentWrap);
      messagesEl?.appendChild(thinkingMsgEl);
      messagesEl && (messagesEl.scrollTop = messagesEl.scrollHeight);
    }
  } else {
    if (thinkingMsgEl) {
      thinkingMsgEl.remove();
      thinkingMsgEl = null;
    }
  }
}

export function setStatus(status: 'loading' | 'ready' | 'error'): void {
  const dot = shadowRoot?.querySelector('.coign-status-dot') as HTMLElement | null;
  if (dot) dot.dataset.status = status;
}

export function setUpgradeStatus(status: 'quick' | 'full' | 'upgrading' | ''): void {
  if (!upgradeStatusEl) return;
  if (status === 'quick') upgradeStatusEl.textContent = 'Quick';
  else if (status === 'full') upgradeStatusEl.textContent = 'Full';
  else if (status === 'upgrading') upgradeStatusEl.textContent = 'Upgrading…';
  else upgradeStatusEl.textContent = '';
}

export function setOnOpenCallback(cb: (() => void) | null): void {
  onOpenCallback = cb;
}

/* ------------------------------------------------------------------ */
/*  Streaming assistant message                                       */
/* ------------------------------------------------------------------ */

export function startAssistantStream(): void {
  if (!messagesEl) return;
  if (thinkingMsgEl) {
    thinkingMsgEl.remove();
    thinkingMsgEl = null;
  }
  isThinking = false;

  streamMsgEl = createEl('div', 'coign-message coign-message--assistant coign-message--streaming');
  const avatar = createEl('div', 'coign-message-avatar');
  avatar.textContent = 'A';
  streamMsgEl.appendChild(avatar);
  const contentWrap = createEl('div', 'coign-message-content');
  streamTextEl = createEl('div', 'coign-message-text');
  streamTextEl.textContent = '';
  contentWrap.appendChild(streamTextEl);
  streamMsgEl.appendChild(contentWrap);
  messagesEl.appendChild(streamMsgEl);
  messagesEl.scrollTop = messagesEl.scrollHeight;
}

export function appendStreamToken(token: string): void {
  if (!streamTextEl) startAssistantStream();
  if (streamTextEl) {
    streamTextEl.textContent = (streamTextEl.textContent ?? '') + token;
    if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight;
  }
}

export function finishAssistantStream(): void {
  if (streamMsgEl) {
    streamMsgEl.classList.remove('coign-message--streaming');
  }
  streamMsgEl = null;
  streamTextEl = null;
  isThinking = false;
  if (inputEl) {
    inputEl.disabled = false;
    inputEl.placeholder = 'Ask a question\u2026';
  }
  const sendBtn = formEl?.querySelector('.coign-send') as HTMLButtonElement | null;
  if (sendBtn) sendBtn.disabled = false;
}

export function hasActiveStream(): boolean {
  return streamMsgEl !== null;
}

/* ------------------------------------------------------------------ */
/*  Theming                                                           */
/* ------------------------------------------------------------------ */

function parseCssColor(input: string): { r: number; g: number; b: number } | null {
  const s = input.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (rgb) {
    return { r: +rgb[1], g: +rgb[2], b: +rgb[3] };
  }
  return null;
}

function relativeLuminance(color: string): number | null {
  const rgb = parseCssColor(color);
  if (!rgb) return null;
  const lin = [rgb.r, rgb.g, rgb.b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** When host sets bg/text, fill surface tokens so OS dark-mode media rules cannot clash. */
function applySurfaceCompanions(target: HTMLElement, bg: string): void {
  const lum = relativeLuminance(bg);
  const light = lum === null ? true : lum > 0.45;
  if (light) {
    target.style.setProperty('--coign-bg-secondary', '#f9fafb');
    target.style.setProperty('--coign-bg-hover', '#f3f4f6');
    target.style.setProperty('--coign-border', '#e5e7eb');
    target.style.setProperty('--coign-text-secondary', '#4b5563');
    target.style.setProperty('--coign-shadow', '0 10px 40px rgba(0, 0, 0, 0.15)');
    target.style.setProperty('color-scheme', 'light');
  } else {
    target.style.setProperty('--coign-bg-secondary', '#1f2937');
    target.style.setProperty('--coign-bg-hover', '#374151');
    target.style.setProperty('--coign-border', '#374151');
    target.style.setProperty('--coign-text-secondary', '#9ca3af');
    target.style.setProperty('--coign-shadow', '0 10px 40px rgba(0, 0, 0, 0.5)');
    target.style.setProperty('color-scheme', 'dark');
  }
}

function formatRadius(radius: number | string): string {
  if (typeof radius === 'number') return `${radius}px`;
  const s = String(radius).trim();
  // Bare numbers → px; already-unitful values pass through
  if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
  return s;
}

export function applyTheme(theme: CoignTheme | undefined, root?: HTMLElement): void {
  const target = root ?? (shadowRoot?.querySelector('.coign-widget') as HTMLElement | null);
  if (!target) return;

  const hasColors = !!(theme && (theme.accent || theme.text || theme.bg));
  const hasRadius = !!(theme && theme.radius !== undefined);

  // Empty / cleared theme → drop custom overrides and restore OS scheme CSS
  if (!theme || (!hasColors && !hasRadius)) {
    const props = [
      '--coign-accent',
      '--coign-accent-hover',
      '--coign-text',
      '--coign-bg',
      '--coign-bg-secondary',
      '--coign-bg-hover',
      '--coign-border',
      '--coign-text-secondary',
      '--coign-shadow',
      '--coign-radius',
      'color-scheme',
    ];
    for (const p of props) target.style.removeProperty(p);
    delete target.dataset.coignTheme;
    return;
  }

  if (theme.accent) {
    target.style.setProperty('--coign-accent', theme.accent);
    target.style.setProperty('--coign-accent-hover', theme.accent);
  }
  if (theme.text) target.style.setProperty('--coign-text', theme.text);
  if (theme.bg) {
    target.style.setProperty('--coign-bg', theme.bg);
    applySurfaceCompanions(target, theme.bg);
  } else if (theme.text) {
    // Text-only themes still need secondary that contrasts with OS surfaces
    const lum = relativeLuminance(theme.text);
    if (lum !== null && lum < 0.4) {
      // Dark text → assume light surfaces
      applySurfaceCompanions(target, '#ffffff');
      target.style.setProperty('--coign-bg', '#ffffff');
    } else if (lum !== null) {
      applySurfaceCompanions(target, '#111827');
      target.style.setProperty('--coign-bg', '#111827');
    }
  }
  if (theme.radius !== undefined) {
    target.style.setProperty('--coign-radius', formatRadius(theme.radius));
  }

  // Only suppress OS dark-mode CSS when host forced bg/text (full surface ownership)
  if (theme.bg || theme.text) {
    target.dataset.coignTheme = 'custom';
  } else {
    delete target.dataset.coignTheme;
  }
}

/* ------------------------------------------------------------------ */
/*  destroyWidget                                                     */
/* ------------------------------------------------------------------ */

export function destroyWidget(): void {
  detachLoadingOverlay();

  if (focusTrapCleanup) {
    focusTrapCleanup();
    focusTrapCleanup = null;
  }

  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
    resizeHandler = null;
  }

  if (hostElement) {
    hostElement.remove();
    hostElement = null;
  }

  shadowRoot = null;
  bubbleEl = null;
  panelEl = null;
  messagesEl = null;
  formEl = null;
  inputEl = null;
  onSubmitCallback = null;
  isPanelOpen = false;
  isInline = false;
  originalParent = null;
  lastFocusedElement = null;
  isThinking = false;
  thinkingMsgEl = null;
  streamMsgEl = null;
  streamTextEl = null;
  onOpenCallback = null;
  upgradeStatusEl = null;
}

/* ------------------------------------------------------------------ */
/*  showWidget / hideWidget                                           */
/* ------------------------------------------------------------------ */

export function showWidget(): void {
  hostElement?.classList.remove('coign-host--hidden');
}

export function hideWidget(): void {
  hostElement?.classList.add('coign-host--hidden');
}

/* ------------------------------------------------------------------ */
/*  openPanel / closePanel                                            */
/* ------------------------------------------------------------------ */

export function openPanel(): void {
  if (!panelEl || isPanelOpen) return;
  if (isInline) {
    // panel is always open in inline mode
    onOpenCallback?.();
    return;
  }

  isPanelOpen = true;
  lastFocusedElement = document.activeElement;

  panelEl.classList.add('coign-panel--open');
  bubbleEl?.classList.add('coign-bubble--hidden');

  onOpenCallback?.();

  // Focus input after animation
  requestAnimationFrame(() => {
    inputEl?.focus();
  });

  focusTrapCleanup = startFocusTrap(panelEl);
}

export function closePanel(): void {
  if (!panelEl || !isPanelOpen) return;
  if (isInline) {
    // panel stays open in inline mode
    return;
  }

  isPanelOpen = false;
  panelEl.classList.remove('coign-panel--open');
  bubbleEl?.classList.remove('coign-bubble--hidden');

  if (focusTrapCleanup) {
    focusTrapCleanup();
    focusTrapCleanup = null;
  }

  if (lastFocusedElement && 'focus' in lastFocusedElement) {
    (lastFocusedElement as HTMLElement).focus();
  }
  lastFocusedElement = null;
}

/* ------------------------------------------------------------------ */
/*  mountInline / unmountInline                                       */
/* ------------------------------------------------------------------ */

export function mountInline(selector: string): void {
  if (!hostElement || !shadowRoot) return;

  const target = document.querySelector(selector);
  if (!target) {
    console.warn(`[Coign] mountInline: selector "${selector}" not found.`);
    return;
  }

  const root = shadowRoot.querySelector('.coign-widget') as HTMLElement | null;
  if (!root) return;

  if (!isInline) {
    originalParent = hostElement.parentElement as HTMLElement;
  }

  isInline = true;
  target.appendChild(hostElement);
  root.classList.add('coign--inline');

  bubbleEl?.classList.add('coign-bubble--hidden');
  panelEl?.classList.add('coign-panel--inline-open');

  requestAnimationFrame(() => {
    inputEl?.focus();
  });
}

export function unmountInline(): void {
  if (!hostElement || !isInline) return;

  const root = shadowRoot?.querySelector('.coign-widget') as HTMLElement | null;
  if (root) root.classList.remove('coign--inline');

  if (originalParent && !originalParent.contains(hostElement)) {
    originalParent.appendChild(hostElement);
  } else if (hostElement.parentElement !== document.body) {
    document.body.appendChild(hostElement);
  }

  isInline = false;
  bubbleEl?.classList.remove('coign-bubble--hidden');
  panelEl?.classList.remove('coign-panel--inline-open');
}

/* ------------------------------------------------------------------ */
/*  Message helpers                                                   */
/* ------------------------------------------------------------------ */

export function addUserMessage(content: string): void {
  addMessage('user', content);
}

export function addAssistantMessage(content: string): void {
  setThinking(false);
  // Avoid duplicating a streamed message
  if (streamMsgEl && streamTextEl) {
    streamTextEl.textContent = content;
    finishAssistantStream();
    return;
  }
  addMessage('assistant', content);
}

export function addToolCallMessage(toolName: string, args?: string): void {
  const text = args ? `${toolName}\n${args}` : toolName;
  addMessage('tool', text, `Call: ${toolName}`);
}

export function addToolResultMessage(toolName: string, result?: string): void {
  const text = result ?? '(no result)';
  addMessage('tool', text, `Result: ${toolName}`);
}

export function clearMessages(): void {
  if (!messagesEl) return;
  messagesEl.innerHTML = '';
}

function addMessage(role: 'user' | 'assistant' | 'tool', content: string, label?: string): void {
  if (!messagesEl) return;

  const msg = createEl('div', `coign-message coign-message--${role}`);

  const avatar = createEl('div', 'coign-message-avatar');
  avatar.textContent = role === 'user' ? 'U' : role === 'assistant' ? 'A' : 'T';
  msg.appendChild(avatar);

  const contentWrap = createEl('div', 'coign-message-content');

  if (label) {
    const labelEl = createEl('div', 'coign-message-tool-label');
    labelEl.textContent = label;
    contentWrap.appendChild(labelEl);
  }

  const textEl = createEl('div', 'coign-message-text');
  textEl.textContent = content;
  contentWrap.appendChild(textEl);

  msg.appendChild(contentWrap);
  messagesEl.appendChild(msg);
  messagesEl.scrollTop = messagesEl.scrollHeight;
}

/* ------------------------------------------------------------------ */
/*  Focus trap                                                        */
/* ------------------------------------------------------------------ */

function startFocusTrap(container: HTMLElement): () => void {
  const selector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const getFocusable = (): HTMLElement[] =>
    Array.from(container.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute('disabled') && (el as HTMLElement).tabIndex >= 0
    ) as HTMLElement[];

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const els = getFocusable();
    if (els.length === 0) return;
    const first = els[0];
    const last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  container.addEventListener('keydown', onKeydown);

  // focus first focusable element
  const els = getFocusable();
  if (els.length > 0) {
    els[0].focus();
  }

  return () => {
    container.removeEventListener('keydown', onKeydown);
  };
}

/* ------------------------------------------------------------------ */
/*  Inline style for host visibility                                  */
/* ------------------------------------------------------------------ */

const hostStyle = document.createElement('style');
hostStyle.textContent = `
  #coign-widget-host.coign-host--hidden { display: none !important; }
`;
document.head.appendChild(hostStyle);
